import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, SEASON_OPTIONS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Season Refresh page — accessible from the sidebar so a steward can revisit
 * "What has God entrusted to you?" whenever a new chapter begins.
 * Same UI as onboarding but stays in the app shell.
 */
export default function Season() {
    const { user, refreshUser } = useAuth();
    const [selected, setSelected] = useState(user?.seasons || []);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { if (user?.seasons) setSelected(user.seasons); }, [user?.seasons]);

    const toggle = (s) =>
        setSelected((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

    const submit = async () => {
        if (selected.length === 0) return;
        setBusy(true);
        try {
            await api.post("/season", { seasons: selected });
            await refreshUser();
            toast.success("Your season is set.");
            navigate("/dashboard");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-16">
            <motion.header
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-3xl"
            >
                <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(var(--eden-gold))] mb-4">Your season</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">
                    What has God entrusted to you now?
                </h1>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                    Seasons shift. Return here whenever a new chapter begins — your dashboard, blueprint,
                    and daily words will gently reshape around what you name here.
                </p>
                {user?.seasons?.length > 0 && (
                    <p className="mt-6 text-sm text-muted-foreground" data-testid="current-season">
                        <span className="uppercase tracking-widest text-[10px]">Currently</span> · {user.seasons.join(" · ")}
                    </p>
                )}
            </motion.header>

            <div className="grid sm:grid-cols-2 gap-2 max-w-3xl" data-testid="season-options-grid">
                {SEASON_OPTIONS.map((s, i) => {
                    const active = selected.includes(s);
                    return (
                        <motion.button
                            key={s}
                            onClick={() => toggle(s)}
                            data-testid={`season-option-${i}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.03 }}
                            className={`text-left px-5 py-4 rounded-md border transition-colors ${
                                active
                                    ? "bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] border-[hsl(var(--eden-forest))]"
                                    : "bg-transparent border-foreground/15 hover:border-foreground/40"
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${active ? "bg-[hsl(var(--eden-gold))] border-[hsl(var(--eden-gold))]" : "border-foreground/30"}`}>
                                    {active && <Check className="w-3 h-3 text-[hsl(var(--eden-forest))]" strokeWidth={2} />}
                                </div>
                                <span className="text-sm md:text-base">{s}</span>
                            </div>
                        </motion.button>
                    );
                })}
            </div>

            <div className="flex items-center gap-4">
                <Button
                    onClick={submit}
                    disabled={selected.length === 0 || busy}
                    data-testid="season-save-btn"
                    className="h-12 px-8 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md text-xs uppercase tracking-[0.3em]"
                >
                    Update my season
                </Button>
                <span className="text-xs text-muted-foreground">{selected.length} selected</span>
            </div>
        </div>
    );
}
